/** Categories reprises des onglets de l'interface web Mailinblack. */
export type Category =
  | 'attente'
  | 'newsletter'
  | 'spearphishing'
  | 'banni'
  | 'spam'
  | 'infecte'
  | 'delivre'
  | 'autre';

export interface CachedSpool {
  id: string;
  subject: string;
  sender: string;
  recipient: string;
  participants: string[];
  createdAt: number | null;
  category: Category;
  store: string | null;
  smtpClientIp: string | null;
  details: Record<string, unknown> | null;
  isNewsletter: boolean;
  read: boolean;
}

export interface StoreStats {
  size: number;
  lastSyncAt: number | null;
  lastFullSyncAt: number | null;
  oldestAt: number | null;
  newestAt: number | null;
}

export interface SearchResponse {
  items: CachedSpool[];
  total: number;
  stats: StoreStats;
}

const BASE_URL: string = (import.meta.env.VITE_API_URL ?? 'http://localhost:4111/api').replace(/\/+$/, '');
const TOKEN: string = import.meta.env.VITE_API_TOKEN ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${TOKEN}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });
  } catch (cause) {
    throw new ApiError(0, `Serveur injoignable (${(cause as Error).message}). Vérifiez VITE_API_URL.`);
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const message = (payload as { error?: string } | null)?.error ?? `Erreur HTTP ${response.status}`;
    throw new ApiError(response.status, message);
  }
  return payload as T;
}

export interface Mailbox {
  address: string;
  count: number;
}

export const api = {
  /**
   * Quarantaine d'une boîte — correspondance exacte, sans filtre de date.
   *
   * Mailinblack ne conserve qu'environ un mois de quarantaine : filtrer sur une
   * fenêtre plus courte n'apportait rien, on affiche donc tout ce que le cache
   * contient pour la catégorie demandée.
   */
  mine: (mailboxes: string[], category: Category, limit = 500) => {
    const params = new URLSearchParams({
      mailboxes: mailboxes.join(','),
      category,
      limit: String(limit),
    });
    return call<SearchResponse & { mailboxes: string[]; counts: Record<Category, number> }>(
      `/spools/mine?${params}`,
    );
  },

  /** Tout ce que l'API Mailinblack renvoie pour un message. */
  detail: (id: string) => call<{ id: string; raw: Record<string, unknown> }>(`/spools/${encodeURIComponent(id)}`),

  /** Boîtes présentes en quarantaine, pour le réglage « ma boîte ». */
  mailboxes: (query: string) =>
    call<{ items: Mailbox[]; total: number }>(`/mailboxes?q=${encodeURIComponent(query)}`),

  stats: () => call<StoreStats>('/spools/stats'),

  /** Indique si une clé en écriture est configurée côté serveur. */
  capabilities: () => call<{ canAct: boolean }>('/capabilities'),

  refresh: () => call<{ status: string; stats: StoreStats }>('/spools/refresh', { method: 'POST' }),

  retrieve: (ids: string[]) =>
    call<{ released: number }>('/spools/retrieve', { method: 'POST', body: JSON.stringify({ ids }) }),

  authorize: (ids: string[]) =>
    call<{ authorized: number }>('/spools/authorize', { method: 'POST', body: JSON.stringify({ ids }) }),
};

/** Lien vers l'interface Mailinblack, ouvert via Front.openUrl. */
export function mailinblackUrl(): string | null {
  const base = (import.meta.env.VITE_MIB_URL ?? '').replace(/\/+$/, '');
  return base || null;
}
